import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { IMarketplace } from "../../../interfaces/IMarketplace";
import { IIntegracaoWhatsApp } from "../../../interfaces/IIntegracaoMarketplace";
import { useMarketplaces } from "../../../hooks/useMarketplaces";
import { useSnackbar } from "../../../../core/contexts/SnackbarContext";
import { useCopiar } from "../../../../core/hooks/useCopiar";
import { SecaoConfiguracao } from "../../../../core/components/SecaoConfiguracao";
import { LinhaDado } from "../../../pages/DetalhesMarketplace/components/LinhaDado";
import { ContentCopy, Visibility, VisibilityOff, VpnKeyOutlined } from "@mui/icons-material";

interface ConfigurarCredenciaisWhatsAppProps {
    marketplace: IMarketplace;
    refresh: () => void;
}

/**
 * Credenciais da instância do WhatsApp.
 *
 * Serve tanto ao primeiro cadastro quanto à alteração posterior: sem o modo de
 * edição, trocar de instância exigia mexer no banco.
 */
export const ConfigurarCredenciaisWhatsApp = ({ marketplace, refresh }: ConfigurarCredenciaisWhatsAppProps) => {
    const integracao = marketplace?.configuracao?.integracao as IIntegracaoWhatsApp | undefined;
    const configurada = !!(integracao?.instanciaId || integracao?.instanciaToken);

    const [editando, setEditando] = useState(!configurada);
    const [instanciaId, setInstanciaId] = useState(integracao?.instanciaId ?? "");
    const [instanciaToken, setInstanciaToken] = useState(integracao?.instanciaToken ?? "");
    const [tokenVisivel, setTokenVisivel] = useState(false);

    const { salvar, loading } = useMarketplaces();
    const notificar = useSnackbar();
    const copiarInstancia = useCopiar(integracao?.instanciaId ?? "", "ID da instância copiado");

    // O refresh do container remonta com os dados novos; sem isso o formulário
    // continuaria mostrando o que foi digitado antes de salvar.
    useEffect(() => {
        setInstanciaId(integracao?.instanciaId ?? "");
        setInstanciaToken(integracao?.instanciaToken ?? "");
    }, [integracao?.instanciaId, integracao?.instanciaToken]);

    const idAlterado = configurada && instanciaId.trim() !== (integracao?.instanciaId ?? "");

    const handleCancelar = () => {
        setInstanciaId(integracao?.instanciaId ?? "");
        setInstanciaToken(integracao?.instanciaToken ?? "");
        setTokenVisivel(false);
        setEditando(false);
    };

    const handleSalvar = async () => {
        if (loading) return;

        const idLimpo = instanciaId.trim();
        const tokenLimpo = instanciaToken.trim();

        if (!idLimpo.length) {
            return notificar("O ID da instância é obrigatório", { severity: "error" });
        }

        if (!tokenLimpo.length) {
            return notificar("O token da instância é obrigatório", { severity: "error" });
        }

        const salvo = await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                integracao: {
                    // Preserva comportamentos, mensagens rápidas e restrições:
                    // o backend regrava a configuração inteira a partir do payload.
                    ...(integracao ?? {}),
                    instanciaId: idLimpo,
                    instanciaToken: tokenLimpo,
                    // Instância nova é outra conexão — o status é reavaliado no
                    // próximo polling da seção de conexão.
                    conectado: idAlterado ? false : (integracao?.conectado ?? false),
                }
            }
        });

        if (!salvo) return;

        notificar(
            configurada ? "Credenciais da instância atualizadas" : "Credenciais salvas",
            { severity: "success" }
        );
        setTokenVisivel(false);
        setEditando(false);
        refresh();
    };

    if (configurada && !editando) {
        return (
            <SecaoConfiguracao
                titulo="Credenciais da instância"
                descricao="Dados usados para conversar com a API do WhatsApp"
                icone={<VpnKeyOutlined />}
                acoes={
                    <Button size="small" variant="outlined" onClick={() => setEditando(true)}>
                        Alterar
                    </Button>
                }
            >
                <LinhaDado rotulo="ID da instância">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
                        <Typography variant="body2" fontWeight={500} sx={{ fontFamily: "monospace" }}>
                            {integracao?.instanciaId ?? "-"}
                        </Typography>
                        <Tooltip title="Copiar ID da instância">
                            <IconButton size="small" onClick={copiarInstancia}>
                                <ContentCopy fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </LinhaDado>
                <LinhaDado rotulo="Token da instância">
                    <Typography variant="body2" fontWeight={500}>
                        {integracao?.instanciaToken ? "•••••••••••• configurado" : "-"}
                    </Typography>
                </LinhaDado>
            </SecaoConfiguracao>
        );
    }

    return (
        <SecaoConfiguracao
            titulo="Credenciais da instância"
            descricao={configurada
                ? "Altere o ID e o token da instância do WhatsApp"
                : "Primeiro passo para ativar o atendimento pelo WhatsApp"}
            icone={<VpnKeyOutlined />}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {configurada ? (
                    <Alert severity="warning" variant="outlined" sx={{ borderRadius: "12px" }}>
                        <Typography variant="caption">
                            Trocar a instância derruba a conexão atual: o número precisará ser conectado
                            de novo por QR Code. Os comportamentos, mensagens rápidas e restrições são mantidos.
                        </Typography>
                    </Alert>
                ) : (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: "12px" }}>
                        <Typography variant="caption">
                            Informe o ID e o token da instância do WhatsApp. Depois de salvar, você poderá
                            conectar o número por QR Code e configurar o atendimento automático.
                        </Typography>
                    </Alert>
                )}

                <TextField
                    label="ID da instância"
                    size="small"
                    fullWidth
                    onChange={(e) => setInstanciaId(e.target.value)}
                    value={instanciaId}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontFamily: "monospace" } }}
                />
                <TextField
                    label="Token da instância"
                    size="small"
                    fullWidth
                    type={tokenVisivel ? "text" : "password"}
                    onChange={(e) => setInstanciaToken(e.target.value)}
                    value={instanciaToken}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        style: { fontFamily: "monospace" },
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={tokenVisivel ? "Ocultar token" : "Mostrar token"}>
                                    <IconButton size="small" onClick={() => setTokenVisivel(!tokenVisivel)} edge="end">
                                        {tokenVisivel ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        )
                    }}
                />

                <Box sx={{ display: "flex", gap: 1 }}>
                    {configurada && (
                        <Button
                            sx={{ height: "44px" }}
                            fullWidth
                            variant="outlined"
                            disabled={loading}
                            onClick={handleCancelar}
                        >
                            Cancelar
                        </Button>
                    )}
                    <Button
                        sx={{ height: "44px" }}
                        fullWidth
                        variant={loading ? "outlined" : "contained"}
                        disabled={loading}
                        onClick={handleSalvar}
                    >
                        {loading ? <CircularProgress size={18} /> : configurada ? "Salvar alterações" : "Salvar credenciais"}
                    </Button>
                </Box>
            </Box>
        </SecaoConfiguracao>
    );
}
